import type { InfiniteData } from '@tanstack/react-query';

import {
  Container,
  ContainerDisplayStyle,
  ContainerDisplayType,
  ContainerItemsPage,
  LinkImage,
  Screen,
  ScreenContent,
} from '../api/generated/types_web';

export type ScreenResponse = {
  screen: Screen;
};

export type ScreenViewData = {
  screen: Screen | undefined;
  title: string | undefined;
  dcoCorrelationId: string | null | undefined;
  liveContent: ScreenContent | undefined;
  gridContainer: Container | undefined;
  filteredScreenContents: ScreenContent[];
  promoTeaser: Container | undefined;
  hasRenderableScreenContents: boolean;
  paginationPlaceholderStyle: string | undefined;
};

const CONTAINER_STYLES_REFETCH_ON_RETURN = new Set([
  ContainerDisplayStyle.Mylist,
  ContainerDisplayStyle.Continuewatching,
  ContainerDisplayStyle.Live,
  ContainerDisplayStyle.LiveUpcoming,
]);

const SUBNAV_CONTAINER_STYLE = 'SUBNAV' as const;
const CONTAINER_ITEM_TYPES = new Set([
  'MediaMetadata',
  'CollectionMetadata',
  'ContentMetadata',
  'PromotionalTeaser',
  'LinkImage',
  'LinkText',
  'LiveChannel',
]);

export function selectScreenQueryData(
  data: InfiniteData<ScreenResponse>
): ScreenViewData {
  return buildTransformedScreenData(mergePaginatedScreenPages(data.pages));
}

/** First DCO correlation id RTE returned for a screen; later pages must reuse it. */
export function getStableDcoCorrelationId(pages: ScreenResponse[]): string | null {
  return (
    pages.find((page) => page.screen?.screenContentsPage?.dcoCorrelationId)?.screen
      ?.screenContentsPage?.dcoCorrelationId ?? null
  );
}

export function mergePaginatedScreenPages(
  pages: ScreenResponse[] | undefined
): Screen | undefined {
  if (!pages?.length) return undefined;

  const pageScreens = pages
    .map((page) => page.screen)
    .filter((screen): screen is Screen => !!screen);
  const firstPageScreen = pageScreens[0];
  const latestPageScreen = pageScreens[pageScreens.length - 1];

  if (!firstPageScreen || !latestPageScreen) return undefined;

  const mergedScreenContents = pageScreens.flatMap(
    (screen) => screen.screenContentsPage?.screenContents ?? []
  );
  const stableDcoCorrelationId = getStableDcoCorrelationId(pages);

  return {
    ...firstPageScreen,
    navigation: firstPageScreen.navigation,
    screenContentsPage: {
      ...firstPageScreen.screenContentsPage,
      cursor: latestPageScreen.screenContentsPage?.cursor ?? null,
      dcoCorrelationId: stableDcoCorrelationId,
      screenContents: mergedScreenContents,
    },
  };
}

export function buildTransformedScreenData(screen: Screen | undefined): ScreenViewData {
  const screenContents = screen?.screenContentsPage?.screenContents ?? [];
  const navigationContainer = createNavigationContainer(screen);
  const filteredScreenContents = filterScreenContents(
    screenContents,
    navigationContainer
  );
  const containers = screenContents.filter(isContainer);
  const promoTeaser = getPromoTeaser(screenContents);

  return {
    screen,
    title: screen?.title,
    dcoCorrelationId: screen?.screenContentsPage?.dcoCorrelationId,
    liveContent: screenContents.find((content) => content.__typename === 'Live'),
    gridContainer: getGridContainer(containers),
    filteredScreenContents,
    promoTeaser,
    hasRenderableScreenContents: hasRenderableScreenContents(filteredScreenContents),
    paginationPlaceholderStyle: getPaginationPlaceholderStyle(filteredScreenContents),
  };
}

function createNavigationContainer(screen: Screen | undefined): Container | null {
  const navigationLinks = screen?.navigation?.navigationLinks ?? [];

  if (navigationLinks.length === 0) return null;

  const itemsPage: ContainerItemsPage = {
    __typename: 'ContainerItemsPage',
    cursor: null,
    totalItemsCount: navigationLinks.length,
    items: navigationLinks,
  };

  return {
    id: screen?.navigation?.id || 'screen-navigation',
    __typename: 'Container',
    style: SUBNAV_CONTAINER_STYLE as ContainerDisplayStyle,
    displayTitle: null,
    displayType: SUBNAV_CONTAINER_STYLE as ContainerDisplayStyle,
    containerItemsPage: itemsPage,
  } as unknown as Container;
}

function filterScreenContents(
  screenContents: ScreenContent[],
  navigationContainer: Container | null
): ScreenContent[] {
  const filtered = screenContents.filter((content) => {
    if (content.__typename === 'LinkImage') {
      return isPromoBanner(content);
    }

    if (['Container', 'LiveChannel'].includes(content.__typename as string)) {
      return (
        supportsAtLeastOneItem(content as Container) &&
        (content as Container).style !== ContainerDisplayStyle.Teaser
      );
    }

    return true;
  });

  return navigationContainer ? [navigationContainer, ...filtered] : filtered;
}

function hasRenderableScreenContents(screenContents: ScreenContent[]): boolean {
  return screenContents.some((content) => {
    if (content.__typename !== 'Container') {
      return false;
    }

    const container = content as Container;

    return (
      (container.style as string) !== SUBNAV_CONTAINER_STYLE &&
      !!container.containerItemsPage?.items?.length
    );
  });
}

function getPaginationPlaceholderStyle(
  screenContents: ScreenContent[]
): string | undefined {
  const lastRenderableContainer = [...screenContents].reverse().find((content) => {
    if (content.__typename !== 'Container') return false;

    const container = content as Container;
    return (
      (container.style as string) !== SUBNAV_CONTAINER_STYLE &&
      supportsAtLeastOneItem(container)
    );
  }) as Container | undefined;

  return lastRenderableContainer?.style;
}

function isContainer(el: ScreenContent): el is Container {
  return el.__typename === 'Container';
}

function containerShouldFetch(container: Container): boolean {
  const style = container.style as ContainerDisplayStyle;

  return (
    style === ContainerDisplayStyle.Mylist ||
    style === ContainerDisplayStyle.Continuewatching ||
    style === ContainerDisplayStyle.Live ||
    style === ContainerDisplayStyle.LiveUpcoming
  );
}

function supportsAtLeastOneItem(container: Container): boolean {
  if (containerShouldFetch(container)) {
    return true;
  }

  return (
    container.containerItemsPage?.items?.some(
      (element) =>
        typeof element.__typename === 'string' &&
        CONTAINER_ITEM_TYPES.has(element.__typename)
    ) ?? false
  );
}

function isPromoBanner(el: ScreenContent): el is LinkImage {
  return (
    el.__typename === 'LinkImage' &&
    (el.imagesByBreakpoint?.some((item) => item?.breakpoint === 'XL') ?? false) &&
    'action' in el &&
    el.action !== null
  );
}

function getPromoTeaser(screenContents: ScreenContent[]): Container | undefined {
  const promoTeaser = screenContents
    .filter(isContainer)
    .find((content) => content.style === ContainerDisplayStyle.Teaser);

  return (promoTeaser?.containerItemsPage?.items?.length ?? 0) > 0
    ? promoTeaser
    : undefined;
}

function getGridContainer(containers: Container[]): Container | undefined {
  if (containers.length !== 1) {
    return undefined;
  }

  return containers.find(
    (container) => container.displayType === ContainerDisplayType.Grid
  );
}
